import React, { useEffect, useRef, useState } from 'react';
import { Button, Spinner, Box, Input } from '@amzn/awsui-components-react';
import renderMarkdown from './AIInsightMarkdown';
import { useAIInsightChatContext } from './AIInsightChatContext';
import './AIInsight.scss';

interface AIInsightChatPanelProps {
    /**
     * Suggested prompts. Presentation config rather than conversation state, so it stays a
     * prop while the transcript and in-flight status come from AIInsightChatContext.
     */
    quickActions?: string[];
}

const DEFAULT_QUICK_ACTIONS = [
    'What is the VAR status?',
    'Show MAR failures',
    'LEF entity breakdown',
    'CWB workbook summary',
];

export default function AIInsightChatPanel({
    quickActions = DEFAULT_QUICK_ACTIONS,
}: AIInsightChatPanelProps) {
    const {
        messages,
        loading,
        error,
        pollingReportId,
        pollingElapsed,
        sendMessage,
    } = useAIInsightChatContext();
    const [input, setInput] = useState('');
    const messagesEndRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        // scrollIntoView is unavailable in some non-browser environments (e.g. jsdom).
        const end = messagesEndRef.current;
        if (typeof end?.scrollIntoView === 'function') {
            end.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages]);

    // Input and quick actions are blocked while a response streams or a report is pending,
    // so a second request cannot be queued against an in-flight agent session.
    const busy = loading || !!pollingReportId;

    const handleSend = () => {
        const question = input.trim();
        if (!question || busy) {
            return;
        }
        sendMessage(question);
        setInput('');
    };

    return (
        <div className="ai-insight-chat">
            <div
                className="ai-insight-chat__messages"
                role="log"
                aria-live="polite"
                aria-label="AI Insights conversation"
            >
                {messages.map((msg, i) => (
                    <div key={i} className={`ai-insight-chat__bubble-row ai-insight-chat__bubble-row--${msg.role}`}>
                        <div className={`ai-insight-chat__bubble ai-insight-chat__bubble--${msg.role}`}>
                            {msg.role === 'assistant' ? renderMarkdown(msg.text) : msg.text}
                        </div>
                    </div>
                ))}
                {loading && (
                    <div className="ai-insight-chat__loading">
                        <Spinner size="normal" />
                        <span>Thinking...</span>
                    </div>
                )}
                {pollingReportId && !loading && (
                    <div className="ai-insight-chat__polling">
                        <Spinner size="normal" />
                        <div className="ai-insight-chat__polling-info">
                            <div className="ai-insight-chat__polling-title">Thinking...</div>
                            <div className="ai-insight-chat__polling-detail">
                                Report ID <code>{pollingReportId}</code>
                                {' · '}
                                {Math.floor(pollingElapsed / 1000)}s elapsed
                                {' · '}
                                {`I'll auto-validate once it's ready (up to 5 min).`}
                            </div>
                        </div>
                    </div>
                )}
                {error && (
                    <Box color="text-status-error" margin={{ bottom: 's' }}>
                        <span role="alert">{error}</span>
                    </Box>
                )}
                <div ref={messagesEndRef} />
            </div>

            <div className="ai-insight-chat__input-row">
                <div className="ai-insight-chat__input-field">
                    <Input
                        value={input}
                        ariaLabel="Ask AI Insights a question"
                        onChange={({ detail }) => setInput(detail.value)}
                        onKeyDown={({ detail }) => {
                            if (detail.key === 'Enter') handleSend();
                        }}
                        placeholder={pollingReportId
                            ? 'Waiting for report to be ready...'
                            : 'Ask about VAR, MAR, LEF, CWB reports...'}
                        disabled={busy}
                    />
                </div>
                <Button
                    onClick={handleSend}
                    loading={loading}
                    disabled={!input.trim() || busy}
                    iconName="send"
                    variant="primary"
                >
                    Send
                </Button>
            </div>

            <div className="ai-insight-chat__quick-actions">
                {quickActions.map(q => (
                    <Button
                        key={q}
                        variant="inline-link"
                        onClick={() => sendMessage(q)}
                        disabled={busy}
                    >
                        {q}
                    </Button>
                ))}
            </div>
        </div>
    );
}
